import { useState, useEffect } from "react";
import { format } from "date-fns";
import { de, enUS, pt } from "date-fns/locale";
import { 
  CheckCircle2, Circle, Plus, Trash2, Pin, PinOff, 
  ClipboardList, Users, Wrench, Package, UserCog, Megaphone, Calculator,
  Calendar, Lightbulb, StickyNote, User, AlertTriangle, Clock, 
  ChevronRight, Bell, ShoppingCart, Dumbbell, MoreHorizontal,
  RefreshCw, Star, Flag, Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for task categories
const TASK_CATEGORY_ICONS = {
  general: ClipboardList,
  client: Users,
  equipment: Wrench,
  inventory: Package,
  staff: UserCog,
  marketing: Megaphone,
  finance: Calculator
};

// Icon mapping for note categories
const NOTE_CATEGORY_ICONS = {
  general: StickyNote,
  client: User,
  meeting: Calendar,
  idea: Lightbulb
};

const PRIORITY_COLORS = {
  high: "bg-red-500",
  medium: "bg-yellow-500",
  low: "bg-green-500"
};

const PRIORITY_LABELS = {
  high: "Hoch",
  medium: "Mittel",
  low: "Niedrig"
};

export default function ManagerPage({ onLogout }) {
  const { lang, t } = useLanguage();
  const dateLocale = lang === "pt" ? pt : lang === "en" ? enUS : de;
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Task modal
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    due_date: format(new Date(), "yyyy-MM-dd"),
    category: "general",
    is_recurring: false,
    recurrence_type: null
  });
  
  // Note modal
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({
    content: "",
    category: "general",
    pinned: false
  });
  
  // Reminder modal
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    client_name: "",
    reminder_type: "follow_up",
    note: "",
    due_date: format(new Date(), "yyyy-MM-dd")
  });

  const [showCompleted, setShowCompleted] = useState(false);
  const [showCompletedOverview, setShowCompletedOverview] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadTasks();
    loadNotes();
    loadReminders();
  }, []);

  useEffect(() => {
    loadTasks();
  }, [showCompleted, showCompletedOverview]);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tasks?include_completed=${showCompleted || showCompletedOverview}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Error loading tasks:", error);
    }
  };

  const loadNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error("Error loading notes:", error);
    }
  };

  const loadReminders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reminders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error("Error loading reminders:", error);
    }
  };

  // Task handlers
  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) {
      toast.error("Bitte geben Sie einen Titel ein");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tasks`, taskForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aufgabe erstellt");
      setIsTaskModalOpen(false);
      setTaskForm({
        title: "",
        description: "",
        priority: "medium",
        due_date: format(new Date(), "yyyy-MM-dd"),
        category: "general",
        is_recurring: false,
        recurrence_type: null
      });
      loadTasks();
      loadDashboard();
    } catch (error) {
      toast.error("Fehler beim Erstellen der Aufgabe");
    }
  };

  const toggleTaskComplete = async (task) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/tasks/${task.id}`, {
        is_completed: !task.is_completed
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!task.is_completed) {
        toast.success("Aufgabe erledigt! ✓");
      }
      loadTasks();
      loadDashboard();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Aufgabe wirklich löschen?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aufgabe gelöscht");
      loadTasks();
      loadDashboard();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Note handlers
  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!noteForm.content.trim()) {
      toast.error("Bitte geben Sie einen Inhalt ein");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notes`, noteForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notiz erstellt");
      setIsNoteModalOpen(false);
      setNoteForm({ content: "", category: "general", pinned: false });
      loadNotes();
      loadDashboard();
    } catch (error) {
      toast.error("Fehler beim Erstellen der Notiz");
    }
  };

  const toggleNotePin = async (note) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notes/${note.id}`, {
        pinned: !note.pinned
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadNotes();
      loadDashboard();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Notiz wirklich löschen?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notiz gelöscht");
      loadNotes();
      loadDashboard();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Reminder handlers
  const handleCreateReminder = async (e) => {
    e.preventDefault();
    if (!reminderForm.client_name.trim()) {
      toast.error("Bitte geben Sie einen Kundennamen ein");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/reminders`, reminderForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Erinnerung erstellt");
      setIsReminderModalOpen(false);
      setReminderForm({
        client_name: "",
        reminder_type: "follow_up",
        note: "",
        due_date: format(new Date(), "yyyy-MM-dd")
      });
      loadReminders();
      loadDashboard();
    } catch (error) {
      toast.error("Fehler beim Erstellen der Erinnerung");
    }
  };

  const completeReminder = async (reminderId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/reminders/${reminderId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Erinnerung erledigt");
      loadReminders();
      loadDashboard();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const deleteReminder = async (reminderId) => {
    if (!window.confirm("Erinnerung wirklich löschen?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/reminders/${reminderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Erinnerung gelöscht");
      loadReminders();
      loadDashboard();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const today = new Date();
  const formattedDate = format(today, "EEEE, d. MMMM yyyy", { locale: de });

  return (
    <Layout onLogout={onLogout}>
      <div className="p-4 md:p-6 h-full flex flex-col overflow-hidden" data-testid="manager-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("dashboard_title")}
            </h1>
            <p className="text-slate-600 text-sm mt-1">{formattedDate}</p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => { loadDashboard(); loadTasks(); loadNotes(); loadReminders(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Dumbbell className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-xs text-slate-500">Trainings heute</p>
                    <p className="text-lg font-bold text-blue-600">{dashboard.summary.trainings_today}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`${dashboard.summary.pending_tasks > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <ClipboardList className={`w-5 h-5 ${dashboard.summary.pending_tasks > 0 ? 'text-amber-500' : 'text-green-500'}`} />
                  <div>
                    <p className="text-xs text-slate-500">{t("pending_tasks")}</p>
                    <p className={`text-lg font-bold ${dashboard.summary.pending_tasks > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {dashboard.summary.pending_tasks}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`${dashboard.summary.overdue_tasks > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-5 h-5 ${dashboard.summary.overdue_tasks > 0 ? 'text-red-500' : 'text-green-500'}`} />
                  <div>
                    <p className="text-xs text-slate-500">{t("overdue")}</p>
                    <p className={`text-lg font-bold ${dashboard.summary.overdue_tasks > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {dashboard.summary.overdue_tasks}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`${dashboard.summary.high_priority_tasks > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Flag className={`w-5 h-5 ${dashboard.summary.high_priority_tasks > 0 ? 'text-orange-500' : 'text-green-500'}`} />
                  <div>
                    <p className="text-xs text-slate-500">{t("high_priority")}</p>
                    <p className={`text-lg font-bold ${dashboard.summary.high_priority_tasks > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {dashboard.summary.high_priority_tasks}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`${dashboard.summary.low_stock_count > 0 ? 'bg-purple-50 border-purple-200' : 'bg-green-50 border-green-200'}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <ShoppingCart className={`w-5 h-5 ${dashboard.summary.low_stock_count > 0 ? 'text-purple-500' : 'text-green-500'}`} />
                  <div>
                    <p className="text-xs text-slate-500">{t("low_stock")}</p>
                    <p className={`text-lg font-bold ${dashboard.summary.low_stock_count > 0 ? 'text-purple-600' : 'text-green-600'}`}>
                      {dashboard.summary.low_stock_count}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`${dashboard.summary.pending_reminders > 0 ? 'bg-cyan-50 border-cyan-200' : 'bg-green-50 border-green-200'}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Bell className={`w-5 h-5 ${dashboard.summary.pending_reminders > 0 ? 'text-cyan-500' : 'text-green-500'}`} />
                  <div>
                    <p className="text-xs text-slate-500">Erinnerungen</p>
                    <p className={`text-lg font-bold ${dashboard.summary.pending_reminders > 0 ? 'text-cyan-600' : 'text-green-600'}`}>
                      {dashboard.summary.pending_reminders}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Notification Banners */}
        {dashboard && (
          <div className="space-y-2 mb-4" data-testid="notification-banners">
            {/* Low stock critical banner */}
            {dashboard.low_stock_items && dashboard.low_stock_items.some(i => i.stock_current === 0) && (
              <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg" data-testid="out-of-stock-banner">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">{t("out_of_stock")}</p>
                  <p className="text-xs text-red-600">
                    {dashboard.low_stock_items.filter(i => i.stock_current === 0).map(i => i.name).join(", ")}
                  </p>
                </div>
                <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-100" onClick={() => window.location.href = '/inventory'}>
                  Zum Lager
                </Button>
              </div>
            )}

            {/* Low stock warning banner */}
            {dashboard.summary?.low_stock_count > 0 && !(dashboard.low_stock_items && dashboard.low_stock_items.some(i => i.stock_current === 0)) && (
              <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="low-stock-banner">
                <ShoppingCart className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800">{dashboard.summary.low_stock_count} Produkte unter Sollbestand</p>
                  <p className="text-xs text-amber-600">
                    {dashboard.low_stock_items?.slice(0, 3).map(i => i.name).join(", ")}{dashboard.summary.low_stock_count > 3 ? ` +${dashboard.summary.low_stock_count - 3} weitere` : ""}
                  </p>
                </div>
                <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => window.location.href = '/inventory'}>
                  Zum Lager
                </Button>
              </div>
            )}

            {/* Bioimpedance notifications */}
            {dashboard.bio_notifications && dashboard.bio_notifications.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="bio-notification-banner">
                <Activity className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-800">Bioimpedanz-Hinweise ({dashboard.bio_notifications.length})</p>
                  <p className="text-xs text-blue-600">
                    {dashboard.bio_notifications.slice(0, 2).map(n => n.title).join(" | ")}{dashboard.bio_notifications.length > 2 ? ` +${dashboard.bio_notifications.length - 2} weitere` : ""}
                  </p>
                </div>
                <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100" onClick={() => window.location.href = '/bioimpedance'}>
                  Zur Bioimpedanz
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mb-4">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              Übersicht
            </TabsTrigger>
            <TabsTrigger value="tasks" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              {t("tasks_section")}
              {tasks.filter(t => !t.is_completed).length > 0 && (
                <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700">
                  {tasks.filter(t => !t.is_completed).length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="notes" className="flex items-center gap-2">
              <StickyNote className="w-4 h-4" />
              {t("notes_section")}
            </TabsTrigger>
            <TabsTrigger value="reminders" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              {t("reminders_section")}
              {reminders.length > 0 && (
                <Badge variant="secondary" className="ml-1 bg-cyan-100 text-cyan-700">
                  {reminders.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="flex-1 overflow-auto">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {/* Today's Tasks */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-amber-500" />
                      Heutige Aufgaben
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowCompletedOverview(!showCompletedOverview)}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${showCompletedOverview ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
                        data-testid="overview-show-completed"
                      >
                        <CheckCircle2 className="w-3 h-3 inline mr-1" />
                        {showCompletedOverview ? 'Erledigte' : 'Erledigte'}
                      </button>
                      <Button variant="ghost" size="sm" onClick={() => setIsTaskModalOpen(true)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(showCompletedOverview ? tasks : (dashboard?.tasks || [])).slice(0, 8).map(task => {
                    const CategoryIcon = TASK_CATEGORY_ICONS[task.category] || ClipboardList;
                    return (
                      <div key={task.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50">
                        <button onClick={() => toggleTaskComplete(task)}>
                          {task.is_completed ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : (
                            <Circle className="w-5 h-5 text-slate-300" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${task.is_completed ? 'line-through text-slate-400' : ''}`}>
                            {task.title}
                          </p>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[task.priority]}`} />
                      </div>
                    );
                  })}
                  {(!showCompletedOverview && (!dashboard?.tasks || dashboard.tasks.length === 0)) && (
                    <p className="text-sm text-slate-500 text-center py-4">Keine Aufgaben für heute</p>
                  )}
                  {(showCompletedOverview && tasks.length === 0) && (
                    <p className="text-sm text-slate-500 text-center py-4">Keine Aufgaben</p>
                  )}
                </CardContent>
                <CardFooter className="pt-0">
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => setActiveTab("tasks")}>
                    Alle anzeigen <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardFooter>
              </Card>

              {/* Pinned Notes */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Pin className="w-4 h-4 text-blue-500" />
                      Angeheftete Notizen
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setIsNoteModalOpen(true)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dashboard?.pinned_notes?.map(note => (
                    <div key={note.id} className="p-2 rounded-lg bg-yellow-50 border border-yellow-200">
                      <p className="text-sm text-slate-700 line-clamp-2">{note.content}</p>
                    </div>
                  ))}
                  {(!dashboard?.pinned_notes || dashboard.pinned_notes.length === 0) && (
                    <p className="text-sm text-slate-500 text-center py-4">Keine angehefteten Notizen</p>
                  )}
                </CardContent>
                <CardFooter className="pt-0">
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => setActiveTab("notes")}>
                    Alle Notizen <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardFooter>
              </Card>

              {/* Client Reminders */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Bell className="w-4 h-4 text-cyan-500" />
                      Kunden-Erinnerungen
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setIsReminderModalOpen(true)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {dashboard?.reminders?.map(reminder => (
                    <div key={reminder.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50">
                      <User className="w-4 h-4 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{reminder.client_name}</p>
                        <p className="text-xs text-slate-500">{reminder.reminder_type}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => completeReminder(reminder.id)}>
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      </Button>
                    </div>
                  ))}
                  {(!dashboard?.reminders || dashboard.reminders.length === 0) && (
                    <p className="text-sm text-slate-500 text-center py-4">Keine Erinnerungen</p>
                  )}
                </CardContent>
                <CardFooter className="pt-0">
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => setActiveTab("reminders")}>
                    Alle anzeigen <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardFooter>
              </Card>

              {/* Low Stock Alert */}
              {dashboard?.low_stock_items && dashboard.low_stock_items.length > 0 && (
                <Card className="border-purple-200 bg-purple-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-purple-500" />
                      Lager nachfüllen
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {dashboard.low_stock_items.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 rounded-lg bg-white">
                        <span className="text-sm">{item.name}</span>
                        <Badge variant="outline" className="text-purple-600">
                          {item.stock_current} / {item.stock_target}
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                  <CardFooter className="pt-0">
                    <Button variant="ghost" size="sm" className="w-full" onClick={() => window.location.href = '/inventory'}>
                      Zum Lager <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardFooter>
                </Card>
              )}

              {/* Today's Trainings */}
              {dashboard?.trainings && dashboard.trainings.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Dumbbell className="w-4 h-4 text-green-500" />
                      Trainings heute
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {dashboard.trainings.slice(0, 5).map(training => (
                      <div key={training.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                        <div>
                          <p className="text-sm font-medium">{training.client_name}</p>
                          <p className="text-xs text-slate-500">{training.training_type}</p>
                        </div>
                        <Badge variant="outline">
                          {training.start_time}
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                  <CardFooter className="pt-0">
                    <Button variant="ghost" size="sm" className="w-full" onClick={() => window.location.href = '/trainings'}>
                      Alle Trainings <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardFooter>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="flex-1 overflow-auto">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="showCompleted" 
                    checked={showCompleted}
                    onCheckedChange={(checked) => setShowCompleted(checked)}
                  />
                  <Label htmlFor="showCompleted" className="text-sm">Erledigte anzeigen</Label>
                </div>
                <Button onClick={() => setIsTaskModalOpen(true)} data-testid="add-task-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Neue Aufgabe
                </Button>
              </div>
              
              <div className="space-y-2">
                {tasks.map(task => {
                  const CategoryIcon = TASK_CATEGORY_ICONS[task.category] || ClipboardList;
                  const isOverdue = task.due_date && task.due_date < format(new Date(), "yyyy-MM-dd") && !task.is_completed;
                  
                  return (
                    <div 
                      key={task.id} 
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        task.is_completed ? 'bg-slate-50 border-slate-200' : 
                        isOverdue ? 'bg-red-50 border-red-200' :
                        'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`task-${task.id}`}
                    >
                      <button onClick={() => toggleTaskComplete(task)} className="flex-shrink-0">
                        {task.is_completed ? (
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        ) : (
                          <Circle className="w-6 h-6 text-slate-300 hover:text-slate-400" />
                        )}
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${task.is_completed ? 'line-through text-slate-400' : ''}`}>
                            {task.title}
                          </p>
                          {task.is_recurring && (
                            <Badge variant="outline" className="text-xs">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              {task.recurrence_type === 'daily' ? 'Täglich' : 
                               task.recurrence_type === 'weekly' ? 'Wöchentlich' : 'Monatlich'}
                            </Badge>
                          )}
                        </div>
                        {task.description && (
                          <p className="text-sm text-slate-500 truncate">{task.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            <CategoryIcon className="w-3 h-3 mr-1" />
                            {task.category}
                          </Badge>
                          {task.due_date && (
                            <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                              <Clock className="w-3 h-3 inline mr-1" />
                              {task.due_date}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${PRIORITY_COLORS[task.priority]}`} 
                           title={PRIORITY_LABELS[task.priority]} />
                      
                      <Button variant="ghost" size="sm" onClick={() => deleteTask(task.id)} className="flex-shrink-0">
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  );
                })}
                
                {tasks.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Keine Aufgaben vorhanden</p>
                    <Button variant="link" onClick={() => setIsTaskModalOpen(true)}>
                      Erste Aufgabe erstellen
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="flex-1 overflow-auto">
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setIsNoteModalOpen(true)} data-testid="add-note-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Neue Notiz
                </Button>
              </div>
              
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {notes.map(note => {
                  const CategoryIcon = NOTE_CATEGORY_ICONS[note.category] || StickyNote;
                  
                  return (
                    <Card key={note.id} className={`${note.pinned ? 'border-yellow-300 bg-yellow-50' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <Badge variant="secondary" className="text-xs">
                            <CategoryIcon className="w-3 h-3 mr-1" />
                            {note.category}
                          </Badge>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => toggleNotePin(note)}>
                              {note.pinned ? (
                                <PinOff className="w-4 h-4 text-yellow-600" />
                              ) : (
                                <Pin className="w-4 h-4 text-slate-400" />
                              )}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteNote(note.id)}>
                              <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {note.created_at && format(new Date(note.created_at), "dd.MM.yyyy HH:mm")}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {notes.length === 0 && (
                  <div className="col-span-full text-center py-12 text-slate-500">
                    <StickyNote className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Keine Notizen vorhanden</p>
                    <Button variant="link" onClick={() => setIsNoteModalOpen(true)}>
                      Erste Notiz erstellen
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders" className="flex-1 overflow-auto">
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setIsReminderModalOpen(true)} data-testid="add-reminder-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Neue Erinnerung
                </Button>
              </div>
              
              <div className="space-y-2">
                {reminders.map(reminder => {
                  const isOverdue = reminder.due_date < format(new Date(), "yyyy-MM-dd");
                  
                  return (
                    <div 
                      key={reminder.id} 
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        isOverdue ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
                      }`}
                    >
                      <User className="w-5 h-5 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{reminder.client_name}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Badge variant="outline" className="text-xs">
                            {reminder.reminder_type === 'follow_up' ? 'Nachverfolgung' :
                             reminder.reminder_type === 'birthday' ? 'Geburtstag' :
                             reminder.reminder_type === 'renewal' ? 'Verlängerung' : 'Feedback'}
                          </Badge>
                          <span className={isOverdue ? 'text-red-600' : ''}>
                            <Clock className="w-3 h-3 inline mr-1" />
                            {reminder.due_date}
                          </span>
                        </div>
                        {reminder.note && (
                          <p className="text-sm text-slate-500 mt-1">{reminder.note}</p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => completeReminder(reminder.id)}>
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteReminder(reminder.id)}>
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  );
                })}
                
                {reminders.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Bell className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Keine Erinnerungen vorhanden</p>
                    <Button variant="link" onClick={() => setIsReminderModalOpen(true)}>
                      Erste Erinnerung erstellen
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Task Modal */}
        <Dialog open={isTaskModalOpen} onOpenChange={setIsTaskModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Neue Aufgabe</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <Label>Titel *</Label>
                <Input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Was muss erledigt werden?"
                  data-testid="task-title-input"
                />
              </div>
              <div>
                <Label>Beschreibung</Label>
                <Textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Details zur Aufgabe..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priorität</Label>
                  <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">🔴 Hoch</SelectItem>
                      <SelectItem value="medium">🟡 Mittel</SelectItem>
                      <SelectItem value="low">🟢 Niedrig</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kategorie</Label>
                  <Select value={taskForm.category} onValueChange={(v) => setTaskForm({ ...taskForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">Allgemein</SelectItem>
                      <SelectItem value="client">Kunden</SelectItem>
                      <SelectItem value="equipment">Geräte</SelectItem>
                      <SelectItem value="inventory">Lager</SelectItem>
                      <SelectItem value="staff">Personal</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="finance">Finanzen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Fälligkeitsdatum</Label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="recurring"
                  checked={taskForm.is_recurring}
                  onCheckedChange={(checked) => setTaskForm({ ...taskForm, is_recurring: checked, recurrence_type: checked ? "weekly" : null })}
                />
                <Label htmlFor="recurring">Wiederkehrende Aufgabe</Label>
              </div>
              {taskForm.is_recurring && (
                <Select value={taskForm.recurrence_type || "weekly"} onValueChange={(v) => setTaskForm({ ...taskForm, recurrence_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Täglich</SelectItem>
                    <SelectItem value="weekly">Wöchentlich</SelectItem>
                    <SelectItem value="monthly">Monatlich</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsTaskModalOpen(false)}>Abbrechen</Button>
                <Button type="submit" data-testid="save-task-button">Erstellen</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Note Modal */}
        <Dialog open={isNoteModalOpen} onOpenChange={setIsNoteModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Neue Notiz</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateNote} className="space-y-4">
              <div>
                <Label>Inhalt *</Label>
                <Textarea
                  value={noteForm.content}
                  onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                  placeholder="Ihre Notiz..."
                  rows={4}
                  data-testid="note-content-input"
                />
              </div>
              <div>
                <Label>Kategorie</Label>
                <Select value={noteForm.category} onValueChange={(v) => setNoteForm({ ...noteForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Allgemein</SelectItem>
                    <SelectItem value="client">Kunden</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="idea">Ideen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="pinned"
                  checked={noteForm.pinned}
                  onCheckedChange={(checked) => setNoteForm({ ...noteForm, pinned: checked })}
                />
                <Label htmlFor="pinned">Notiz anheften</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsNoteModalOpen(false)}>Abbrechen</Button>
                <Button type="submit" data-testid="save-note-button">Erstellen</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reminder Modal */}
        <Dialog open={isReminderModalOpen} onOpenChange={setIsReminderModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Neue Kunden-Erinnerung</DialogTitle>
              <DialogDescription>Erstellen Sie eine Erinnerung für Kundenkontakt</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateReminder} className="space-y-4">
              <div>
                <Label>Kundenname *</Label>
                <Input
                  value={reminderForm.client_name}
                  onChange={(e) => setReminderForm({ ...reminderForm, client_name: e.target.value })}
                  placeholder="Name des Kunden"
                  data-testid="reminder-client-input"
                />
              </div>
              <div>
                <Label>Erinnerungstyp</Label>
                <Select value={reminderForm.reminder_type} onValueChange={(v) => setReminderForm({ ...reminderForm, reminder_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="follow_up">Nachverfolgung</SelectItem>
                    <SelectItem value="birthday">Geburtstag</SelectItem>
                    <SelectItem value="renewal">Verlängerung</SelectItem>
                    <SelectItem value="feedback">Feedback einholen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Datum</Label>
                <Input
                  type="date"
                  value={reminderForm.due_date}
                  onChange={(e) => setReminderForm({ ...reminderForm, due_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Notiz</Label>
                <Textarea
                  value={reminderForm.note}
                  onChange={(e) => setReminderForm({ ...reminderForm, note: e.target.value })}
                  placeholder="Zusätzliche Informationen..."
                  rows={2}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsReminderModalOpen(false)}>Abbrechen</Button>
                <Button type="submit" data-testid="save-reminder-button">Erstellen</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
